import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "./sidebar/Sidebar";
import Breadcrumbs from "./Breadcrumbs";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import apiService from "../utils/api";
import Snackbar from '@mui/material/Snackbar';
import AddDocumentModal from "./modals/AddDocumentModal";
import downloadIcon from "../assets/download.svg";
import Loader from "./Loader";

const Templates = () => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({ open:false, success:false, message:"" });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('templatesVisibleColumns');
    const defaults = saved ? JSON.parse(saved) : {
      name: true,
      type: true,
      description: true,
      createdDate: true,
      isActive: true,
      actions: true,
    };
    return defaults;
  });
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);
  const mandatoryColumns = ['name','actions'];
  const uploadRef = useRef(null);
  const [isAddDocOpen, setIsAddDocOpen] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [addFormMax, setAddFormMax] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addFileName, setAddFileName] = useState("");
  const [formValues, setFormValues] = useState({ template_name:"", template_type:"", description:"" });
  const [formErrors, setFormErrors] = useState({});
  const [contractTypes, setContractTypes] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  const getUserId = () => Number(sessionStorage.getItem("user_id")) || null;
  const roleRaw = (sessionStorage.getItem('user_role') || '').replace(/\s+/g,'');
  const isAdmin = roleRaw === 'Admin';
  const isLegal = roleRaw === 'LegalUser' || roleRaw === 'LegalReviewer';

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.fetchData(`templates${filterType ? '' : ''}`);
      const list = Array.isArray(response) ? response : (response.results || response.data || []);
      const getSecureId = () => {
        const cryptoObj = window.crypto || window.msCrypto;
        const array = new Uint32Array(1);
        cryptoObj.getRandomValues(array);
        return array[0].toString();
      };
      
      const formatted = list.map(t => ({
        key: String(t.template_id ?? t.templateId ?? getSecureId()),
        data: {
          template_id: t.template_id ?? t.templateId,
          name: t.template_name ?? t.name,
          type: t.template_type ?? t.type,
          description: t.description || '',
          fileUrl: t.file_url || t.fileUrl,
          createdDate: formatDateYMD(t.created_at || t.createdAt || ''),
          is_active: (t.is_active ?? t.isActive ?? true) ? 1 : 0,
        }
      }));
      setNodes(formatted);
    } catch (e) {
      setSnackbar({ open:true, success:false, message:'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const formatDateYMD = (value) => {
    if (!value) return '';
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0,10);
    } catch(_) {}
    return String(value).slice(0,10);
  };

  useEffect(() => { fetchTemplates(); }, []);
  useEffect(() => { localStorage.setItem('templatesVisibleColumns', JSON.stringify(visibleColumns)); }, [visibleColumns]);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.fetchData('metadata/contract-type');
        const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const names = arr
          .map(r => (r && typeof r === 'object') ? (r.value || r.TypeName || r.name) : r)
          .filter(v => typeof v === 'string' && v.trim().length > 0);
        setContractTypes(names);
      } catch (e) {
        setContractTypes([]);
      }
    })();
  }, []);

  const filteredNodes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return nodes.filter(n => {
      const hay = `${n.data.name||''} ${n.data.type||''} ${n.data.description||''} ${n.data.createdDate||''}`.toLowerCase();
      const matchesSearch = hay.includes(term);
      const matchesType = filterType ? n.data.type === filterType : true;
      return matchesSearch && matchesType;
    });
  }, [nodes, searchTerm, filterType]);

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;
    const a = document.createElement('a');
    a.href = fileUrl; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.click();
  };

  const handlePreview = (fileUrl) => { if (fileUrl) window.open(fileUrl, '_blank'); };

  const handleUse = (row) => {
    setSelectedTemplateName(row.name || '');
    setIsAddDocOpen(true);
  };

  const toggleActive = async (row) => {
    try {
      setLoading(true);
      setTogglingId(row.template_id);
      const newVal = row.is_active ? false : true;
      const json = await apiService.putData(`templates/${row.template_id}`, { is_active: String(newVal) });
      if (!json || json.status !== 'success') throw new Error(json?.message || 'Update failed');
      setSnackbar({ open:true, success:true, message:`Template marked as ${newVal ? 'Active' : 'Inactive'}` });
      await fetchTemplates();
    } catch(e) {
      setSnackbar({ open:true, success:false, message:'Failed to update status' });
    } finally { setLoading(false); setTogglingId(null); }
  };

  // Explicit deactivate helper (requested)
  const deactivateTemplate = async (row) => {
    try {
      setLoading(true);
      setTogglingId(row.template_id);
      const json = await apiService.putData(`templates/${row.template_id}`, { is_active: 'false' });
      if (!json || json.status !== 'success') throw new Error(json?.message || 'Update failed');
      setSnackbar({ open:true, success:true, message:'Template marked as Inactive' });
      await fetchTemplates();
    } catch(e) {
      setSnackbar({ open:true, success:false, message:'Failed to deactivate' });
    } finally { setLoading(false); setTogglingId(null); }
  };

  const createTemplate = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      const payload = { template_name: file.name.replace(/\.[^.]+$/,''), template_type: 'MSA', created_by: String(getUserId()||4268), description: 'creating this for MSA' };
      formData.append('payload', JSON.stringify(payload));
      const json = await apiService.postData('templates', formData);
      if (!json || json.status !== 'success') throw new Error(json?.message||'Error');
      setSnackbar({ open:true, success:true, message:'Template created successfully' });
      await fetchTemplates();
      setSelectedTemplateName(payload.template_name);
      setIsAddDocOpen(true);
    } catch (e) {
      setSnackbar({ open:true, success:false, message:'Failed to create template' });
    } finally { setLoading(false); }
  };

  const validateForm = () => {
    const errs = {};
    if (!addFile) errs.file = 'File is required';
    if (!formValues.template_name) errs.template_name = 'Template name is required';
    if (!formValues.template_type) errs.template_type = 'Template type is required';
    if (!formValues.description) errs.description = 'Description is required';
    else if (formValues.description.length < 10) errs.description = 'Description must be at least 10 characters';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreateFromForm = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', addFile);
      const payload = { template_name: formValues.template_name, template_type: formValues.template_type, created_by: String(getUserId()||4268), description: formValues.description };
      formData.append('payload', JSON.stringify(payload));
      const json = await apiService.postData('templates', formData);
      if (!json || json.status !== 'success') throw new Error(json?.message||'Error');
      setSnackbar({ open:true, success:true, message:'Template created successfully' });
      setAddFormOpen(false);
      setAddFile(null); setAddFileName(""); setFormValues({ template_name:"", template_type:"", description:"" }); setFormErrors({});
      await fetchTemplates();
      setSelectedTemplateName(payload.template_name);
      setIsAddDocOpen(true);
    } catch (e) {
      setSnackbar({ open:true, success:false, message:'Failed to create template' });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, padding: "20px" }}>
        <Breadcrumbs />
        <div style={{ 
          background: '#fff', 
          borderRadius: '8px', 
          padding: '24px 28px', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)', 
          borderLeft: '4px solid #ff6b35', 
          marginBottom: '24px' 
        }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 700, 
            color: '#212529', 
            margin: 0, 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}>
            <span style={{ 
              width: '28px', 
              height: '28px', 
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c61 100%)', 
              borderRadius: '6px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              color: '#fff', 
              fontSize: '16px' 
            }}>
              📋
            </span>
            Available Templates
          </h1>
        </div>

        <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', alignItems:'center', marginBottom:'12px' }}>
          {isAdmin && (
            <>
              <button onClick={()=>setAddFormOpen(true)} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px', fontWeight:600 }}>Add Template</button>
            </>
          )}
          <select value={filterType} onChange={(e)=>setFilterType(e.target.value)} style={{ padding:'8px 12px', border:'1px solid #ff6b35', borderRadius:'6px', minWidth:'160px', outline:'none', color:'#4A4A4A' }} title="Filter by template type">
            <option value="">All Types</option>
            {[...new Set(nodes.map(n=>n.data.type))].map(t=> (<option key={t} value={t}>{t}</option>))}
          </select>
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={{ width:'260px', padding:'8px 12px', border:'1px solid #ff6b35', borderRadius:'6px', outline:'none' }} title="Search across name, type and description" />
          <button onClick={()=>setShowColumnOptions(true)} style={{ cursor:'pointer', padding:'4px 12px', borderRadius:'6px', background:'transparent', color:'#4A4A4A', border:'1px solid #ff6b35', fontSize:'16px', fontWeight:500 }}>⚙ Column options</button>
        </div>

        <Loader 
          isLoading={loading}
          message="Loading Available templates...stay tuned"
        />
        {!loading && (
        <div style={{ overflow:'auto' }}>
          <TreeTable value={filteredNodes} paginator rows={10} rowsPerPageOptions={[10,20,50]} expandedKeys={expandedKeys} onToggle={(e)=>setExpandedKeys(e.value)} showGridlines tableStyle={{ width:'100%', tableLayout:'auto', minWidth:'1200px' }}>
            {visibleColumns.name && (
              <Column field="name" header="Template Name" style={{ width:'24%' }} body={(node)=> (
                <a href={node.data.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: node.data.is_active ? '#2E7D32' : '#C62828', fontWeight:700, textDecoration:'none' }} title="Click to view Template">{node.data.name}</a>
              )} />
            )}
            {visibleColumns.type && (<Column field="type" header="Type" style={{ width:'12%' }} />)}
            {visibleColumns.description && (<Column field="description" header="Description" style={{ width:'30%' }} body={(node)=>{
              const d = node.data.description || '';
              return <span>{d.charAt(0).toUpperCase()+d.slice(1)}</span>;
            }} />)}
            {visibleColumns.createdDate && (<Column field="createdDate" header="Created Date" style={{ width:'12%' }} />)}
            {visibleColumns.actions && (
              <Column header="Actions" style={{ width:'12%' }} body={(node)=> (
                <div style={{ display:'flex', gap:'12px', alignItems:'center', justifyContent:'center' }}>
                  <img src={downloadIcon} alt="Download" title="Download template" style={{ cursor:'pointer', width:16, height:16 }} onClick={()=>handleDownload(node.data.fileUrl)} />
                  <svg onClick={()=>handleUse(node.data)} title="Use template" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor:'pointer' }}>
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                  {!isLegal && (
                    <button onClick={()=> (node.data.is_active ? deactivateTemplate(node.data) : toggleActive(node.data))} title={node.data.is_active ? 'Mark Inactive' : 'Mark Active'} style={{ display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #ff6b35', color:'#ff6b35', background:'transparent', borderRadius:14, padding:'4px 10px', cursor:'pointer' }} disabled={togglingId === node.data.template_id}>
                    {togglingId === node.data.template_id ? (
                      <span className="spinner" style={{ width:14, height:14, border:'2px solid #ffe3d6', borderTopColor:'#ff6b35', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
                    ) : (
                      <span style={{ fontWeight:600 }}>{node.data.is_active ? 'Deactivate' : 'Activate'}</span>
                    )}
                  </button>
                  )}
                </div>
              )} />
            )}
          </TreeTable>
        </div>
        )}

        {showColumnOptions && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', backdropFilter:'blur(6px)', display:'flex', alignItems:'flex-start', justifyContent:'flex-end', zIndex:1000 }}>
            <div style={{ width:'340px', background:'#fff', borderRadius:'8px', overflow:'hidden', marginTop:'100px', marginRight:'20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', fontWeight:700, background:'#ff6b35', color:'#fff' }}>
                <div>Column Options</div>
                <button onClick={()=>setShowColumnOptions(false)} aria-label="Close" style={{ background:'transparent', border:'none', fontSize:'25px', cursor:'pointer', color:'#fff' }}>×</button>
              </div>
              <div style={{ maxHeight:'50vh', overflowY:'auto', padding:'10px 12px' }}>
                {["name","type","description","createdDate","isActive","actions"].map(key => (
                  <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f3f4f6' }}>
                    <span style={{ fontSize:'14px', color:'#111827' }}>{key==='name' ? 'Template Name' : key==='isActive' ? 'Status' : key.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}{mandatoryColumns.includes(key)? <span style={{ color:'#ff6b35' }}> *</span>:''}</span>
                    <button onClick={()=>{ if(mandatoryColumns.includes(key)) return; setTempVisibleColumns(prev=>({ ...prev, [key]: !prev[key]})); }} disabled={mandatoryColumns.includes(key)} aria-label={tempVisibleColumns[key] ? `Hide ${key}` : `Show ${key}`} style={{ background:'transparent', border:'none', cursor: mandatoryColumns.includes(key)?'not-allowed':'pointer', opacity: mandatoryColumns.includes(key)?0.6:1 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                        {!tempVisibleColumns[key] && (<line x1="2" y1="2" x2="18" y2="18" stroke="#000000" strokeWidth="3" />)}
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', padding:'10px 12px', borderTop:'1px solid #e5e7eb' }}>
                <button onClick={()=>{ setTempVisibleColumns(visibleColumns); setShowColumnOptions(false); }} style={{ background:'transparent', color:'#ff6b35', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Cancel</button>
                <button onClick={()=>{ setVisibleColumns({ ...tempVisibleColumns, name:true, actions:true }); localStorage.setItem('templatesVisibleColumns', JSON.stringify({ ...tempVisibleColumns, name:true, actions:true })); setShowColumnOptions(false); }} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Save</button>
              </div>
            </div>
          </div>
        )}

        <AddDocumentModal isOpen={isAddDocOpen} onClose={()=>setIsAddDocOpen(false)} onSelectTemplate={()=>{}} onSave={()=>setIsAddDocOpen(false)} parentNode={null} />

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={()=>setSnackbar(prev=>({ ...prev, open:false }))} message={snackbar.message} sx={{ "& .MuiSnackbarContent-root": { backgroundColor: snackbar.success ? "#4caf50" : "#f44336" }, zIndex:1400 }} />

        {addFormOpen && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500 }}>
            <div style={{ width: addFormMax ? '90vw' : '520px', height: addFormMax ? '90vh' : 'auto', background:'#fff', borderRadius:'10px', boxShadow:'0 10px 25px rgba(0,0,0,0.15)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'12px 16px', background:'#ff6b35', color:'#fff', fontWeight:700, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>Add Template</span>
                <div>
                  <button onClick={()=>setAddFormMax(m=>!m)} title={addFormMax? 'Restore' : 'Maximize'} style={{ marginRight:8, background:'rgba(255,255,255,0.2)', border:'1px solid #fff', color:'#fff', padding:'2px 8px', borderRadius:6, cursor:'pointer' }}>⤢</button>
                  <button onClick={()=>{ setAddFormOpen(false); setAddFile(null); setAddFileName(""); setFormValues({ template_name:"", template_type:"", description:"" }); setFormErrors({}); }} style={{ background:'transparent', border:'none', fontSize:'22px', color:'#fff', cursor:'pointer' }}>×</button>
                </div>
              </div>
              <div style={{ padding:'14px 16px', overflow:'auto' }}>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ fontSize:12, color:'#555' }}>Upload File</label>
                  <div style={{ border:'1px dashed #ff6b35', padding:'10px', borderRadius:'8px', background:'#fff7f3', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ color:'#4A4A4A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{addFileName || 'Choose .pdf, .doc, .docx'}</span>
                    <label style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'6px 12px', borderRadius:'6px', cursor:'pointer' }}>
                      Browse
                      <input type="file" accept=".pdf,.doc,.docx" onChange={(e)=>{ const f=e.target.files&&e.target.files[0]; setAddFile(f||null); setAddFileName(f?f.name:""); if(f && !formValues.template_name) setFormValues(v=>({ ...v, template_name: f.name.replace(/\.[^.]+$/,'') })); }} style={{ display:'none' }} />
                    </label>
                  </div>
                  {formErrors.file && <div style={{ color:'red', fontSize:12 }}>{formErrors.file}</div>}
                </div>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ fontSize:12, color:'#555' }}>Template Name</label>
                  <input value={formValues.template_name} onChange={(e)=>setFormValues(v=>({ ...v, template_name: e.target.value }))} style={{ width:'100%', padding:'8px 10px', border:'1px solid #ff6b35', borderRadius:'6px', outline:'none' }} />
                  {formErrors.template_name && <div style={{ color:'red', fontSize:12 }}>{formErrors.template_name}</div>}
                </div>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ fontSize:12, color:'#555' }}>Template Type</label>
                  <select value={formValues.template_type} onChange={(e)=>setFormValues(v=>({ ...v, template_type: e.target.value }))} style={{ width:'100%', padding:'8px 10px', border:'1px solid #ff6b35', borderRadius:'6px', outline:'none', color:'#4A4A4A', backgroundImage:"url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"12\" height=\"12\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%236b7280\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><polyline points=\"6 9 12 15 18 9\"></polyline></svg>')", backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center', paddingRight:'32px' }}>
                    <option value="">Select type</option>
                    {contractTypes.map(t => (<option key={t} value={t}>{t}</option>))}
                  </select>
                  {formErrors.template_type && <div style={{ color:'red', fontSize:12 }}>{formErrors.template_type}</div>}
                </div>
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ fontSize:12, color:'#555' }}>Description</label>
                  <textarea value={formValues.description} onChange={(e)=>setFormValues(v=>({ ...v, description: e.target.value }))} rows={3} style={{ width:'100%', padding:'8px 10px', border:'1px solid #ff6b35', borderRadius:'6px', outline:'none' }} />
                  {formErrors.description && <div style={{ color:'red', fontSize:12 }}>{formErrors.description}</div>}
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', padding:'12px 16px', borderTop:'1px solid #eee' }}>
                <button onClick={()=>{ setAddFormOpen(false); setAddFile(null); setAddFileName(""); setFormValues({ template_name:"", template_type:"", description:"" }); setFormErrors({}); }} style={{ background:'transparent', color:'#ff6b35', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Cancel</button>
                <button onClick={handleCreateFromForm} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px' }}>Create</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
